import type {
  AppAdapter,
  AppNotification,
  BadgeSummary,
  DocumentSummary,
  HomeSnapshot,
  RevenueSnapshot,
  SessionUser,
  ThanksActivity,
  TrainingModule,
} from './app-adapter'
import { selectDailyLetter, taipeiDateKey, type DailyLetterEntry } from './daily-letter'

const MOCK_USER: SessionUser = {
  id: 'synthetic-employee-001',
  name: 'Preview 使用者',
  storeName: 'Preview 門市',
  role: 'employee',
  roleLabel: '正職一級',
  level: 28,
}

const modules: TrainingModule[] = [
  ['front', '外場', '外場站點訓練', 67, 'in_progress'],
  ['packing', '包餐', '包餐站點訓練', 33, 'in_progress'],
  ['prep', '備料', '備料站點訓練', 0, 'not_started'],
  ['counter', '櫃台', '櫃台站點訓練', 0, 'not_started'],
  ['soup', '湯區', '湯區站點訓練', 0, 'not_started'],
  ['egg', '煎蛋', '煎蛋站點訓練', 0, 'not_started'],
  ['vegetable', '菜區', '菜區站點訓練', 0, 'not_started'],
  ['rice', '飯區', '飯區站點訓練', 0, 'not_started'],
  ['weighing', '秤料', '秤料站點訓練', 0, 'not_started'],
  ['steamed-egg', '蒸盤', '蒸盤站點訓練', 0, 'not_started'],
].map(([id, title, description, progress, status], index) => ({
  id: String(id),
  title: String(title),
  description: String(description),
  progress: Number(progress),
  status: status as TrainingModule['status'],
  areas: index === 0
      ? {
        academic: { state: 'completed', label: 'Preview 合成：已完成', progress: 100 },
        practical: { state: 'pending_review', label: 'Preview 合成：待主管評分', progress: 75 },
        teaching: { state: 'in_progress', label: 'Preview 合成：進行中', progress: 25 },
      }
    : index === 1
      ? {
          academic: { state: 'in_progress', label: 'Preview 合成：進行中', progress: 50 },
          practical: { state: 'not_started', label: 'Preview 合成：尚未開始', progress: 0 },
          teaching: { state: 'not_started', label: 'Preview 合成：尚未開始', progress: 0 },
        }
      : {
          academic: { state: 'not_started', label: 'Preview 合成：尚未開始', progress: 0 },
          practical: { state: 'not_started', label: 'Preview 合成：尚未開始', progress: 0 },
          teaching: { state: 'not_started', label: 'Preview 合成：尚未開始', progress: 0 },
        },
}))

const mockDailyLetters: DailyLetterEntry[] = [
  { id: 'mock-letter-001', order: 1, content: '把每一件簡單的事做好，就是不簡單的自己。', active: true },
  { id: 'mock-letter-002', order: 2, content: '一起把今天照顧好，家的樣子就會慢慢長出來。', active: true },
  { id: 'mock-letter-003', order: 3, content: '專業，是把在意藏進每一個看得見的細節。', active: true },
]

const home: HomeSnapshot = {
  announcement: {
    id: 'synthetic-announcement-growth',
    title: '本月成長任務已更新',
    summary: '請查看學科、術科與帶教的最新狀態。',
  },
  tasks: [
    { id: 'synthetic-open-app', label: 'Preview 操作範例', completed: true },
    { id: 'synthetic-daily-reflection', label: 'Preview 每日反思範例', completed: false },
    { id: 'synthetic-thank-colleague', label: 'Preview 感謝範例', completed: false },
  ],
  dailyLetter: selectDailyLetter(taipeiDateKey(), mockDailyLetters).content,
}

const revenue: RevenueSnapshot = {
  annualRevenue: 60,
  annualTarget: 100,
  stage: '茁壯之家',
  rankings: [
    { rank: 1, store: 'Preview 門市 A', revenue: 60, rate: 60 },
    { rank: 2, store: 'Preview 門市 B', revenue: 40, rate: 40 },
  ],
}

const thanks: ThanksActivity[] = [
  {
    id: 'synthetic-thanks-1', from: 'Preview 夥伴 A', to: 'Preview 夥伴 B', type: 'warmth',
    message: '這是感謝動態的合成預覽內容。', occurredAt: 'Preview 時間',
  },
  {
    id: 'synthetic-thanks-2', from: 'Preview 夥伴 C', to: 'Preview 夥伴 B', type: 'wisdom',
    message: '這是感謝動態的合成預覽內容。', occurredAt: 'Preview 時間',
  },
  {
    id: 'synthetic-thanks-3', from: 'Preview 夥伴 D', to: 'Preview 夥伴 B', type: 'reliability',
    message: '這是感謝動態的合成預覽內容。', occurredAt: 'Preview 時間',
  },
]

const badges: BadgeSummary[] = [
  { id: 'warmth', name: '暖心力', category: '文化力', earned: false, description: '每一次真心感謝，都讓團隊更有力量。' },
  { id: 'wisdom', name: '智慧力', category: '文化力', earned: false, description: '主動分享知識，協助夥伴解決問題。' },
  { id: 'reliability', name: '可靠力', category: '文化力', earned: false, description: '承諾必達，讓夥伴可以安心託付。' },
  { id: 'learning', name: '學習力', category: '成長力', earned: false, description: '持續學習，把能力轉化成穩定表現。' },
  { id: 'service', name: '服務力', category: '專業力', earned: false, description: '以一致標準帶來有溫度的服務。' },
  { id: 'culture', name: '文化力', category: '文化力', earned: false, description: '在每個行動裡實踐 VASO 文化。' },
]

const notifications: AppNotification[] = [
  { id: 'synthetic-notice-1', kind: 'broadcast', title: 'Preview 公告範例', time: 'Preview 時間', read: false, target: '/broadcasts/synthetic-announcement-growth' },
  { id: 'synthetic-notice-2', kind: 'training', title: 'Preview 訓練提醒範例', time: 'Preview 時間', read: false, target: '/training/front' },
  { id: 'synthetic-notice-3', kind: 'badge', title: 'Preview 勳章素材範例', time: 'Preview 時間', read: true, target: '/badges/warmth' },
  { id: 'synthetic-notice-4', kind: 'thanks', title: 'Preview 感謝動態範例', time: 'Preview 時間', read: true, target: '/thanks/activity' },
]

const documents: DocumentSummary[] = [
  { id: 'synthetic-document-sop', title: 'Preview SOP 文件範例', category: 'SOP', acknowledged: false },
  { id: 'synthetic-document-rule', title: 'Preview 規章文件範例', category: '規章', acknowledged: true },
  { id: 'synthetic-document-notice', title: 'Preview 公告文件範例', category: '公告', acknowledged: true },
]

const delay = <T,>(value: T) => new Promise<T>((resolve) => setTimeout(() => resolve(value), 60))

export const mockAdapter: AppAdapter = {
  mode: 'mock',
  getSession: () => delay(MOCK_USER),
  getHome: () => delay(home),
  getTrainingModules: () => delay(modules),
  getRevenue: () => delay(revenue),
  getThanksActivity: () => delay(thanks),
  getBadges: () => delay(badges),
  getNotifications: () => delay(notifications),
  getDocuments: () => delay(documents),
}

export { MOCK_USER }
